/**
 * Backorder Notice Component
 * Handles dynamic variant updates, accessible tooltip popovers, keyboard interaction,
 * mobile tap dismissal, and tag-based purchase unlock gating ('backorder-discount').
 */
(function() {
  'use strict';

  function initSection(wrapper) {
    if (!wrapper || wrapper.dataset.backorderInitialized === 'true') {
      return;
    }
    wrapper.dataset.backorderInitialized = 'true';

    const sectionId = wrapper.dataset.sectionId;
    const inventoryScript = document.getElementById('ProductInventoryJson-' + sectionId);
    let inventoryMap = {};

    if (inventoryScript && inventoryScript.textContent) {
      try {
        inventoryMap = JSON.parse(inventoryScript.textContent);
      } catch (e) {
        console.error('[BackorderNotice] Could not parse inventory JSON for section', sectionId, e);
      }
    }

    const hasUnlockTag = wrapper.dataset.hasTag === 'true';
    const badgeText = wrapper.dataset.badgeText || 'Backorder';
    const discountText = wrapper.dataset.discountText || 'Backorder: eligible items receive 10% off automatically at checkout.';
    const unavailableText = wrapper.dataset.unavailableText || 'Backorder purchasing temporarily unavailable.';

    const unlockedContainer = wrapper.querySelector('[data-backorder-unlocked-container]');
    const lockedContainer = wrapper.querySelector('[data-backorder-locked-container]');
    const popoverWrapper = wrapper.querySelector('[data-backorder-popover-wrapper]');
    const triggerBtn = wrapper.querySelector('[data-backorder-trigger]');
    const tooltipEl = wrapper.querySelector('[data-backorder-tooltip]');
    const textEl = wrapper.querySelector('[data-backorder-text]');
    const closeBtn = wrapper.querySelector('[data-backorder-close]');

    const sectionContainer = document.getElementById('ProductSection-' + sectionId) || wrapper.closest('[data-section-type="product"]') || wrapper.closest('form');
    const form = wrapper.closest('form') || (sectionContainer ? sectionContainer.querySelector('form[action*="/cart/add"]') : null);
    const addToCartBtn = form ? form.querySelector('[data-add-to-cart]') : null;
    const addToCartText = addToCartBtn ? (addToCartBtn.querySelector('[data-add-to-cart-text]') || addToCartBtn) : null;
    const defaultAddToCartText = (addToCartText && addToCartText.textContent.trim()) ? addToCartText.textContent.trim() : 'Add to cart';

    let isOpen = false;
    let hoverTimeout = null;
    let suppressFocusOpen = false;
    let isCurrentlyLocked = false;

    function openTooltip() {
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
        hoverTimeout = null;
      }
      if (isOpen) return;
      isOpen = true;
      if (popoverWrapper) popoverWrapper.classList.add('is-open');
      if (triggerBtn) triggerBtn.setAttribute('aria-expanded', 'true');
      if (tooltipEl) tooltipEl.setAttribute('aria-hidden', 'false');
    }

    function closeTooltip(returnFocus) {
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
        hoverTimeout = null;
      }
      if (!isOpen) return;
      isOpen = false;
      if (popoverWrapper) popoverWrapper.classList.remove('is-open');
      if (triggerBtn) triggerBtn.setAttribute('aria-expanded', 'false');
      if (tooltipEl) tooltipEl.setAttribute('aria-hidden', 'true');
      if (returnFocus && triggerBtn) {
        suppressFocusOpen = true;
        try {
          triggerBtn.focus();
        } catch (_) {}
        setTimeout(function() {
          suppressFocusOpen = false;
        }, 100);
      }
    }

    // 1. Tooltip Interactivity (Hover, Focus, Click, Dismissal)
    if (popoverWrapper && triggerBtn && tooltipEl) {
      // Hover interaction with grace period
      popoverWrapper.addEventListener('mouseenter', function() {
        openTooltip();
      });

      popoverWrapper.addEventListener('mouseleave', function() {
        hoverTimeout = setTimeout(function() {
          closeTooltip(false);
        }, 220);
      });

      // Keyboard focus interaction
      triggerBtn.addEventListener('focus', function() {
        if (suppressFocusOpen) return;
        openTooltip();
      });

      popoverWrapper.addEventListener('focusout', function(e) {
        const nextTarget = e.relatedTarget;
        if (!nextTarget || !popoverWrapper.contains(nextTarget)) {
          closeTooltip(false);
        }
      });

      // Click / Mobile tap interaction
      triggerBtn.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        if (isOpen) {
          closeTooltip(false);
        } else {
          openTooltip();
        }
      });

      // Tooltip close button
      if (closeBtn) {
        closeBtn.addEventListener('click', function(e) {
          e.preventDefault();
          e.stopPropagation();
          closeTooltip(true);
        });
      }

      // Escape key dismissal
      document.addEventListener('keydown', function(e) {
        if (!isOpen) return;
        if (e.key === 'Escape' || e.key === 'Esc') {
          e.preventDefault();
          closeTooltip(true);
        }
      });

      // Outside tap/click dismissal
      document.addEventListener('click', function(e) {
        if (!isOpen) return;
        if (!popoverWrapper.contains(e.target)) {
          closeTooltip(false);
        }
      });
    }

    // 2. Variant evaluation & badge/purchase controls update
    function evaluateVariant(variantId, variantAvailable) {
      if (!variantId) {
        return { isBackorder: false, isSoldOut: false };
      }
      const inv = inventoryMap[String(variantId)];
      if (!inv) {
        return { isBackorder: false, isSoldOut: false };
      }

      // Definition of backorder:
      // - Inventory is tracked (inventory_management != null/blank)
      // - Available inventory is zero or below (inventory_quantity <= 0)
      // - Continue selling when out of stock is enabled (inventory_policy === 'continue')
      const isTracked = Boolean(inv.inventory_management);
      const isZeroOrBelow = typeof inv.inventory_quantity === 'number' && inv.inventory_quantity <= 0;
      const allowsContinue = inv.inventory_policy === 'continue';

      const isBackorder = isTracked && isZeroOrBelow && allowsContinue;
      const isSoldOut = (!variantAvailable || !inv.available) && !allowsContinue;

      return {
        isBackorder: isBackorder,
        isSoldOut: isSoldOut
      };
    }

    function updateDisplay(variant) {
      if (!variant || !variant.id) {
        closeTooltip(false);
        wrapper.classList.add('hide');
        isCurrentlyLocked = false;
        if (form) form.classList.remove('product-form--backorder-locked');
        return;
      }

      const status = evaluateVariant(variant.id, variant.available);

      if (status.isBackorder) {
        wrapper.classList.remove('hide');

        if (hasUnlockTag) {
          // Unlocked Backorder (has 'backorder-discount' tag)
          isCurrentlyLocked = false;
          if (lockedContainer) lockedContainer.classList.add('hide');
          if (unlockedContainer) unlockedContainer.classList.remove('hide');
          if (textEl) textEl.textContent = discountText;

          // Enable purchasing
          if (form) form.classList.remove('product-form--backorder-locked');
          if (addToCartBtn) {
            addToCartBtn.disabled = false;
            addToCartBtn.removeAttribute('aria-disabled');
            addToCartBtn.setAttribute('aria-label', defaultAddToCartText);
            if (addToCartText) addToCartText.innerHTML = defaultAddToCartText;
          }
        } else {
          // Locked Backorder (no 'backorder-discount' tag)
          isCurrentlyLocked = true;
          closeTooltip(false);
          if (unlockedContainer) unlockedContainer.classList.add('hide');
          if (lockedContainer) lockedContainer.classList.remove('hide');

          // Keep purchase controls visible but disabled
          if (form) form.classList.add('product-form--backorder-locked');
          if (addToCartBtn) {
            addToCartBtn.disabled = true;
            addToCartBtn.setAttribute('aria-disabled', 'true');
            addToCartBtn.setAttribute('aria-label', unavailableText);
            if (addToCartText) addToCartText.innerHTML = unavailableText;
          }
        }
      } else {
        // Not backorder (either in stock or sold out)
        isCurrentlyLocked = false;
        closeTooltip(false);
        if (unlockedContainer) unlockedContainer.classList.add('hide');
        if (lockedContainer) lockedContainer.classList.add('hide');
        wrapper.classList.add('hide');
        if (form) form.classList.remove('product-form--backorder-locked');

        // Restore normal text if returning from locked backorder to available
        if (!status.isSoldOut && addToCartBtn && !addToCartBtn.disabled) {
          if (addToCartText && addToCartText.innerHTML === unavailableText) {
            addToCartText.innerHTML = defaultAddToCartText;
            addToCartBtn.setAttribute('aria-label', defaultAddToCartText);
          }
        }
      }
    }

    // Intercept form submission if customer tries to bypass disabled state on locked backorder
    if (form) {
      form.addEventListener('submit', function(e) {
        if (isCurrentlyLocked) {
          e.preventDefault();
          e.stopPropagation();
          const lockedNotice = wrapper.querySelector('[data-backorder-locked-container]');
          if (lockedNotice) {
            try {
              lockedNotice.scrollIntoView({ behavior: 'smooth', block: 'center' });
            } catch (_) {}
          }
          return false;
        }
      });
    }

    // 3. Variant Change Listeners
    if (sectionContainer) {
      sectionContainer.addEventListener('variantChange', function(evt) {
        if (evt && evt.detail) {
          updateDisplay(evt.detail.variant);
        }
      });
    }

    // Fallback: master select change listener
    if (form) {
      const masterSelect = form.querySelector('select[name="id"]');
      if (masterSelect) {
        masterSelect.addEventListener('change', function() {
          const selectedId = masterSelect.value;
          const inv = inventoryMap[selectedId];
          const available = inv ? inv.available : true;
          updateDisplay({ id: selectedId, available: available });
        });

        // Sync initial state on script initialization
        if (masterSelect.value) {
          const initialInv = inventoryMap[masterSelect.value];
          if (initialInv) {
            updateDisplay({ id: masterSelect.value, available: initialInv.available });
          }
        }
      }
    }
  }

  function initAll() {
    const wrappers = document.querySelectorAll('[data-backorder-wrapper]');
    wrappers.forEach(function(w) {
      initSection(w);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Theme Editor section reload support
  document.addEventListener('shopify:section:load', function(e) {
    if (e.target) {
      const wrapper = e.target.querySelector('[data-backorder-wrapper]');
      if (wrapper) {
        wrapper.dataset.backorderInitialized = 'false';
        initSection(wrapper);
      }
    }
  });
})();
